"use client";

import React, { useState, useEffect, useMemo } from "react";
import { Check, Copy, RefreshCw, Wallet, AlertTriangle, ExternalLink, Smartphone } from "lucide-react";
import { useRouter } from "next/navigation";
import { QRCodeSVG } from "qrcode.react";
import AlternatePaymentOptions from "./AlternatePaymentOptions";
import { PAYMENT_METHOD, type PaymentMethodId } from "@/lib/flutterwave_v4/payment_config";
import { useFlutterwavePaymentPoll } from "@/hooks/useFlutterwavePaymentPoll";
import { useLanguage } from "@/contexts/LanguageContext";
import { getStrings } from "@/i18n/dictionaries";

interface MobileWalletDetailsProps {
  paymentUrl?: string;
  walletType: "opay" | "momo-gh" | "momo-ke" | "momo-rw" | "momo-ug" | "momo-zm" | "momo-tz" | "momo-xof" | "momo-xaf";
  amountNgn: number;
  txRef: string;
  onComplete: () => void;
  onCancel: () => void;
  onSwitchToStripe?: () => void;
  onSwitchToBankTransfer?: () => void;
  onSwitchToOpay?: () => void;
  onSwitchToMomo?: () => void;
  currency?: string;
  status?: string;
  instructions?: string;
  countryCode?: string;
  currentMethod?: PaymentMethodId;
  /** Flutterwave redirect URL the user must visit to authorize the charge (MoMo redirect-auth flow) */
  authorizationRedirect?: string | null;
  /** Base64 QR image from Flutterwave (Orange Money Senegal) */
  qrImage?: string | null;
}

export default function MobileWalletDetails({
  paymentUrl,
  walletType,
  amountNgn,
  txRef,
  onComplete,
  onCancel,
  onSwitchToStripe,
  onSwitchToBankTransfer,
  onSwitchToOpay,
  onSwitchToMomo,
  currency = "NGN",
  status,
  instructions,
  countryCode,
  currentMethod = PAYMENT_METHOD.MOMO,
  authorizationRedirect,
  qrImage,
}: MobileWalletDetailsProps) {
  const router = useRouter();
  const { locale } = useLanguage();
  const t = useMemo(() => getStrings(locale, "payment"), [locale]);
  const [hasOpenedWallet, setHasOpenedWallet] = useState(false);
  const [hasAuthorized, setHasAuthorized] = useState(false);
  const [restoredFromStorage, setRestoredFromStorage] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // ── localStorage key shared with PricingTable for resume-after-refresh ──
  const PENDING_MOMO_STORAGE_KEY = "jobolar_pending_momo_payment";

  // ── Shared polling hook ───────────────────────────────────
  const poll = useFlutterwavePaymentPoll({
    txRef,
    onConfirmed: () => {
      try { localStorage.removeItem(PENDING_MOMO_STORAGE_KEY); } catch (_) {}
      onComplete();
      setTimeout(() => router.push("/dashboard"), 1000);
    },
  });
  const isPolling = poll.isChecking;
  const timeoutReached = poll.outcome === "timeout";
  const underpaidDetail = poll.outcome === "underpaid" ? poll.detail : null;
  const isFailed = poll.outcome === "failed";
  const failedDetail = isFailed ? poll.detail : null;

  // Generic MoMo redirect-based authorization flow (any country, not Ghana-specific)
  const isRedirectAuth = Boolean(authorizationRedirect) && walletType.startsWith("momo-");
  // Has any QR source (base64 from Flutterwave, or we can generate one from authorizationRedirect)
  const hasQrSource = Boolean(qrImage) || Boolean(authorizationRedirect);
  // Detect if this is a pending MoMo with instructions (non-redirect flow)
  const isPendingWithInstructions = status === "pending" && instructions && !paymentUrl && !isRedirectAuth;
  // True when we have NEITHER a redirect URL NOR a QR — pure phone-prompt flow
  const isPhonePromptOnly = !isRedirectAuth && !qrImage && isPendingWithInstructions;

  // Validate and parse amount
  const safeAmount = useMemo(() => {
    const num = typeof amountNgn === "number"
      ? amountNgn
      : typeof amountNgn === "string"
        ? Number(amountNgn)
        : NaN;
    if (!Number.isFinite(num) || num <= 0) {
      console.warn("[MobileWalletDetails] Invalid amount received:", { amountNgn, type: typeof amountNgn });
      return null;
    }
    return num;
  }, [amountNgn]);

  // Currency symbol lookup
  const currencySymbol = useMemo(() => {
    const symbols: Record<string, string> = {
      NGN: "₦", GHS: "GH₵", KES: "KSh", UGX: "USh ", RWF: "FRw ",
      TZS: "TSh ", ZMW: "ZK ", XOF: "CFA ", XAF: "FCFA ",
    };
    return symbols[currency.toUpperCase()] || currency.toUpperCase();
  }, [currency]);

  // Wallet display name
  const walletName = walletType === "momo-ke" ? "M-Pesa" : walletType === "opay" ? "OPay" : "Mobile Money";

  // ── Color theme ──
  const isKenya = walletType === "momo-ke" && currency === "KES";
  const isOpay = walletType === "opay";
  const theme = useMemo(() => {
    if (isKenya) return { accent: "emerald", icon: "bg-emerald-50 text-emerald-600", border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-800", btn: "bg-emerald-600 hover:bg-emerald-500" };
    if (isOpay)  return { accent: "purple",  icon: "bg-purple-50 text-purple-600",   border: "border-purple-200",  bg: "bg-purple-50",  text: "text-purple-800",  btn: "bg-purple-600 hover:bg-purple-500" };
    return               { accent: "orange",  icon: "bg-orange-50 text-orange-600",   border: "border-orange-200",  bg: "bg-orange-50",  text: "text-orange-800",  btn: "bg-orange-600 hover:bg-orange-500" };
  }, [isKenya, isOpay]);

  // ── Handlers ──
  const handleOpenWallet = () => {
    window.open(paymentUrl, "_blank");
    setHasOpenedWallet(true);
  };

  const handleAuthorize = () => {
    if (!authorizationRedirect) return;
    window.open(authorizationRedirect, "_blank", "noopener,noreferrer");
    setHasAuthorized(true);
    if (!isPolling) {
      console.log("[MobileWalletDetails] Starting poll after MoMo authorization redirect");
      poll.start();
    }
  };

  const handleCopyLink = () => {
    if (authorizationRedirect) {
      navigator.clipboard?.writeText(authorizationRedirect).catch(() => {});
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const handleIHavePaid = () => poll.start();

  // ── Auto-polling effects ──
  useEffect(() => {
    if (isPendingWithInstructions && !isPolling) {
      console.log("[MobileWalletDetails] Auto-starting polling for pending MoMo");
      poll.start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPendingWithInstructions]);

  useEffect(() => {
    if (isRedirectAuth && !isPolling && !hasAuthorized) {
      try {
        const saved = localStorage.getItem(PENDING_MOMO_STORAGE_KEY);
        if (saved) {
          setRestoredFromStorage(true);
          console.log("[MobileWalletDetails] Restored from localStorage — auto-starting poll");
          poll.start();
        }
      } catch (_) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRedirectAuth]);

  useEffect(() => {
    if (isPendingWithInstructions && !restoredFromStorage) {
      try {
        const saved = localStorage.getItem(PENDING_MOMO_STORAGE_KEY);
        if (saved) {
          setRestoredFromStorage(true);
          console.log("[MobileWalletDetails] Restored pending MoMo from localStorage");
        }
      } catch (_) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPendingWithInstructions]);

  useEffect(() => {
    if (isFailed || timeoutReached) {
      try { localStorage.removeItem(PENDING_MOMO_STORAGE_KEY); } catch (_) {}
    }
  }, [isFailed, timeoutReached]);

  // ── Derived values ──
  const effectiveCountryCode = countryCode || (walletType === "opay" ? "NG" : walletType === "momo-xof" ? "XOF" : walletType === "momo-xaf" ? "XAF" : walletType.replace("momo-", "").toUpperCase());
  const showAlternates = Boolean(onSwitchToStripe || onSwitchToBankTransfer || onSwitchToOpay || onSwitchToMomo);
  const isVerifyScreen = isPendingWithInstructions || isRedirectAuth;

  // Determine heading
  const heading = restoredFromStorage && isPolling
    ? t.verifying_payment
    : isRedirectAuth
      ? (hasAuthorized ? t.waiting_approval : t.authorize_payment)
      : isPendingWithInstructions
        ? t.waiting_approval
        : t.pay_with_wallet.replace("{wallet}", walletName);

  const subheading = restoredFromStorage && isPolling
    ? t.pending_checking
    : isRedirectAuth
      ? (hasAuthorized
          ? t.complete_auth_phone
          : t.click_authorize_desc)
      : isPhonePromptOnly
        ? t.approve_phone
        : t.complete_in_app.replace("{wallet}", walletName);

  // ── Helpful error hints ──
  const errorHint = useMemo(() => {
    if (!isFailed && !timeoutReached) return null;
    const detail = failedDetail || "";
    if (detail.includes("PAYER_NOT_FOUND") || detail.toLowerCase().includes("payer not found"))
      return "This phone number may not be registered with the selected mobile money network. Verify your number and network, then retry.";
    if (detail.includes("TARGET_AUTHORIZATION_ERROR") || detail.toLowerCase().includes("authorization"))
      return "The mobile money authorization was not completed. Please be ready to approve on your phone, then retry.";
    if (timeoutReached)
      return "We haven't received confirmation yet. If you already approved on your phone, wait a few minutes and check your subscription status. Otherwise, retry.";
    return null;
  }, [isFailed, timeoutReached, failedDetail]);

  // ──────────────────────────────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3">
      <div
        className={`relative w-full rounded-xl bg-white shadow-2xl overflow-y-auto ${
          isVerifyScreen ? "max-w-2xl max-h-[80vh]" : "max-w-md max-h-[90vh]"
        }`}
      >
        {/* ── Header (always full-width) ── */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-4 py-3 flex items-center gap-3">
          <div className={`rounded-full p-2 ${theme.icon}`}>
            <Wallet className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-slate-900 leading-tight">{heading}</h2>
            <p className="text-xs text-slate-500 truncate">{subheading}</p>
          </div>
          {/* Resuming badge */}
          {restoredFromStorage && isPolling && (
            <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-2 py-0.5 text-[10px] font-medium text-blue-700">
              <RefreshCw className="h-2.5 w-2.5 animate-spin" /> {t.resuming}
            </span>
          )}
        </div>

        {/* ── Body ── */}
        <div className={`p-4 ${isVerifyScreen ? "grid gap-4 grid-cols-1 md:grid-cols-2" : ""}`}>

          {/* ═══════ LEFT COL: Amount + Unified Instructions + QR/Authorize ═══════ */}
          <div className="space-y-3">
            {/* Amount card */}
            <div className={`rounded-lg border ${theme.border} ${theme.bg} p-3`}>
              <p className={`text-[10px] font-semibold uppercase ${theme.text} opacity-70`}>{t.amount_to_pay}</p>
              <p className={`text-xl font-bold ${theme.text}`}>
                {safeAmount !== null ? `${currencySymbol}${safeAmount.toLocaleString()}` : `${currencySymbol}—`}
              </p>
            </div>

            {/* Unified instructions card */}
            {isVerifyScreen && (
              <div className={`rounded-lg border ${theme.border} ${theme.bg} p-3`}>
                <p className={`text-[10px] font-semibold uppercase ${theme.text} opacity-70 mb-1`}>
                  {t.payment_instructions}
                </p>
                {isRedirectAuth ? (
                  <ol className={`space-y-0.5 text-xs ${theme.text} list-decimal list-inside leading-snug`}>
                    <li>{t.click_authorize_below}</li>
                    <li>{t.approve_momo_prompt}</li>
                    <li>{t.keep_open_60s}</li>
                    <li>{t.no_prompt_retry}</li>
                  </ol>
                ) : isPendingWithInstructions && instructions ? (
                  <div className={`text-xs ${theme.text} leading-snug space-y-1.5`}>
                    <p>{instructions}</p>
                    <ul className={`list-disc list-inside ${theme.text} opacity-80`}>
                      <li>{t.approve_momo_prompt.replace("Mobile Money ", "")}</li>
                      <li>{t.keep_page_open}</li>
                      <li>{t.no_prompt_retry}</li>
                    </ul>
                  </div>
                ) : (
                  <ol className={`space-y-0.5 text-xs ${theme.text} list-decimal list-inside leading-snug`}>
                    <li>{t.open_wallet_below.replace("{wallet}", walletName)}</li>
                    <li>{t.complete_in_app_step}</li>
                    <li>{t.return_click_paid}</li>
                  </ol>
                )}
              </div>
            )}

            {/* Non-verify instructions (simple wallet flow, kept as-is) */}
            {!isVerifyScreen && (
              <div className={`rounded-lg border ${theme.border} ${theme.bg} p-3`}>
                <p className={`text-[10px] font-semibold uppercase ${theme.text} opacity-70 mb-1`}>{t.how_to_pay}</p>
                <ol className={`space-y-0.5 text-xs ${theme.text} list-decimal list-inside leading-snug`}>
                  <li>{t.open_wallet_below.replace("{wallet}", walletName)}</li>
                  <li>{t.complete_in_app_step}</li>
                  <li>{t.return_click_paid}</li>
                </ol>
              </div>
            )}

            {/* QR / Authorize in left column (if applicable) */}
            {isVerifyScreen && (isRedirectAuth || qrImage) && (
              <div className="rounded-lg border border-slate-200 bg-white p-3 flex flex-col items-center gap-2">
                <p className="text-[10px] font-medium text-slate-500 uppercase">
                  {qrImage ? t.scan_qr_pay : t.scan_authorize}
                </p>
                {qrImage ? (
                  <img
                    src={qrImage.startsWith("data:") ? qrImage : `data:image/png;base64,${qrImage}`}
                    alt="Payment QR code"
                    className="w-36 h-36 object-contain"
                  />
                ) : authorizationRedirect ? (
                  <QRCodeSVG value={authorizationRedirect} size={144} level="M" includeMargin />
                ) : null}
                {authorizationRedirect && (
                  <button
                    onClick={handleCopyLink}
                    className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700 transition"
                    title="Copy authorization link"
                  >
                    {linkCopied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
                    {linkCopied ? t.copied : t.copy_link}
                  </button>
                )}
              </div>
            )}

            {/* Phone-prompt indicator (no QR, no redirect) in left column */}
            {isVerifyScreen && isPhonePromptOnly && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-center gap-3">
                <Smartphone className="h-6 w-6 text-amber-600 shrink-0" />
                <p className="text-xs text-amber-800 leading-snug">
                  {t.check_phone_momo}
                </p>
              </div>
            )}

            {/* Authorize button (when redirect-auth) */}
            {isVerifyScreen && isRedirectAuth && (
              <>
                <button
                  onClick={handleAuthorize}
                  disabled={isPolling && hasAuthorized}
                  className={`w-full rounded-lg ${theme.btn} px-3 py-2.5 text-sm font-semibold text-white shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                >
                  {isPolling && hasAuthorized ? (
                    <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> {t.waiting_auth}</>
                  ) : (
                    <><ExternalLink className="h-3.5 w-3.5" /> {hasAuthorized ? t.re_open_auth : t.authorize_payment}</>
                  )}
                </button>
                {hasAuthorized && isPolling && !isFailed && !timeoutReached && authorizationRedirect && (
                  <button
                    onClick={() => window.open(authorizationRedirect, "_blank", "noopener,noreferrer")}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 transition flex items-center justify-center gap-1.5"
                  >
                    <ExternalLink className="h-3 w-3" /> {t.open_link_again}
                  </button>
                )}
              </>
            )}

            {/* Security note */}
            <p className="text-[10px] text-slate-400 flex items-center gap-1">
              <Check className="h-3 w-3 text-emerald-500 shrink-0" />
              {t.secure_flutterwave_short}
            </p>
          </div>

          {/* ═══════ RIGHT COL: Status + Retry + Alternates ═══════ */}
          {isVerifyScreen && (
            <div className="space-y-3">
              {/* Status card */}
              <div className={`rounded-lg border ${theme.border} ${theme.bg} p-3`}>
                <p className={`text-[10px] font-semibold uppercase ${theme.text} opacity-70 mb-1`}>{t.status_label}</p>

                {isPolling && !isFailed && !timeoutReached && !underpaidDetail && (
                  <p className={`text-xs ${theme.text} flex items-center gap-1.5`}>
                    <RefreshCw className="h-3 w-3 animate-spin shrink-0" />
                    {t.checking_confirmation}
                  </p>
                )}

                {isFailed && (
                  <div className="space-y-1.5">
                    <p className="text-xs text-red-700 font-semibold flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3 shrink-0" /> {t.payment_failed}
                    </p>
                    <p className="text-[11px] text-red-600 leading-snug">
                      {failedDetail || t.auth_not_completed}
                    </p>
                    {errorHint && (
                      <p className="text-[11px] text-red-500 italic leading-snug">{errorHint}</p>
                    )}
                    <button
                      onClick={() => {
                        try { localStorage.removeItem(PENDING_MOMO_STORAGE_KEY); } catch (_) {}
                        poll.stop();
                        onCancel();
                      }}
                      className="mt-1 w-full rounded-md bg-red-600 px-2 py-1.5 text-xs font-semibold text-white hover:bg-red-500 transition flex items-center justify-center gap-1"
                    >
                      <RefreshCw className="h-3 w-3" /> {t.retry_payment}
                    </button>
                  </div>
                )}

                {timeoutReached && (
                  <div className="space-y-1.5">
                    <p className="text-xs text-amber-700 font-semibold">{t.confirmation_pending}</p>
                    <p className="text-[11px] text-amber-600 leading-snug">
                      {errorHint || t.timeout_short}
                    </p>
                  </div>
                )}

                {underpaidDetail && (
                  <p className="text-xs text-red-700 font-semibold">{underpaidDetail}</p>
                )}

                {!isPolling && !isFailed && !timeoutReached && !underpaidDetail && (
                  <p className="text-xs text-slate-500">{t.waiting_to_authorize}</p>
                )}
              </div>

              {/* Alternate payment options */}
              {showAlternates && (
                <AlternatePaymentOptions
                  countryCode={effectiveCountryCode}
                  currentMethod={currentMethod}
                  onSwitchToStripe={onSwitchToStripe}
                  onSwitchToBankTransfer={onSwitchToBankTransfer}
                  onSwitchToOpay={onSwitchToOpay}
                  onSwitchToMomo={onSwitchToMomo}
                  disabled={false}
                />
              )}

              {showAlternates && (
                <p className="text-[10px] text-slate-400 text-center">
                  {t.secure_flutterwave}
                </p>
              )}
            </div>
          )}
        </div>

        {/* ── Non-verify screen action buttons (OPay / simple wallet) ── */}
        {!isVerifyScreen && (
          <div className="px-4 pb-4 space-y-2">
            {paymentUrl && (
              <>
                <button
                  onClick={handleOpenWallet}
                  disabled={isPolling}
                  className={`w-full rounded-lg ${theme.btn} px-3 py-2.5 text-sm font-semibold text-white shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                >
                  <Wallet className="h-4 w-4" /> {t.open_wallet.replace("{wallet}", walletName)}
                </button>
                {hasOpenedWallet && (
                  <button
                    onClick={handleIHavePaid}
                    disabled={isPolling}
                    className="w-full rounded-lg bg-emerald-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isPolling ? <><RefreshCw className="h-4 w-4 animate-spin" /> {t.checking_payment}</> : t.i_have_paid}
                  </button>
                )}
              </>
            )}
            {isPolling && (
              <div className={`rounded-lg ${theme.bg} border ${theme.border} p-2`}>
                <p className={`text-xs ${theme.text} text-center flex items-center justify-center gap-1`}>
                  <RefreshCw className="h-3 w-3 animate-spin" /> {t.checking_confirmation}
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── Footer: Cancel ── */}
        <div className="sticky bottom-0 z-10 bg-white border-t border-slate-100 px-4 py-2.5">
          <button
            onClick={() => {
              try { localStorage.removeItem(PENDING_MOMO_STORAGE_KEY); } catch (_) {}
              poll.stop();
              onCancel();
            }}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition"
          >
            {t.cancel}
          </button>
        </div>
      </div>
    </div>
  );
}

